import { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../../../common/index.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { useGetQuizzesQuery } from '../../../../../../redux/services/quizzes.jsx';
import {
  useGetQuizQuestionQuery,
  useSaveQuizQuestionMutation,
  useGetQuizQuestionCorrectAnswersQuery,
} from '../../../../../../redux/services/quiz_questions.jsx';
import QuestionForm from './QuestionForm.jsx';

function useQuery() {
  return new URLSearchParams(window.location.search);
}

const QuestionsEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams(); // optional; if present -> edit mode, else create mode
  const query = useQuery();
  const quizIdFromQuery = query.get('quizId');
  const quizId = quizIdFromQuery ? Number(quizIdFromQuery) : undefined;

  const isEdit = Boolean(id);

  useEffect(() => {
    document.title =
      (isEdit ? t('Úprava otázky') : t('Pridanie otázky')) + ' - FRI CMS';
  }, [isEdit, t]);

  const { saveSuccess, saveFailed } = useToastMessages();

  const { data: quizzes, isLoading: isQuizzesLoading } = useGetQuizzesQuery();
  // fetch question only in edit mode
  const { data: question, isLoading: isQuestionLoading } =
    useGetQuizQuestionQuery(id, { skip: !isEdit });
  const { data: correctData, isLoading: isCorrectLoading } =
    useGetQuizQuestionCorrectAnswersQuery(
      { id: Number(id) },
      { skip: !isEdit },
    );
  const [saveQuestion, { isSuccess, isError, error }] =
    useSaveQuizQuestionMutation();

  useEffect(() => {
    if (isSuccess) {
      saveSuccess();
      const targetQuizId = isEdit
        ? question?.fv_quiz_id
        : quizIdFromQuery
          ? Number(quizIdFromQuery)
          : undefined;
      // Navigate back to quizzes list and request expansion of the quiz containing this question
      if (targetQuizId) {
        navigate(routes.cmsQuizzes, { state: { expandQuizId: targetQuizId } });
      } else {
        navigate(routes.cmsQuizzes);
      }
    }
    if (isError) {
      saveFailed(error);
    }
  }, [isSuccess, isError]);

  const quizOptions = useMemo(() => {
    const list = Array.isArray(quizzes)
      ? quizzes
      : Array.isArray(quizzes?.data)
        ? quizzes.data
        : [];
    return list.map((q) => ({
      value: q.id,
      label: q.title || `${t('Kvíz')} #${q.id}`,
    }));
  }, [quizzes, t]);

  if ((isEdit && (isQuestionLoading || isCorrectLoading)) || isQuizzesLoading) {
    return <Spinner />;
  }

  const mapInitialAnswers = (q) => {
    const list = Array.isArray(q?.answers) ? q.answers : [];
    if (!list.length) return undefined;
    const correctIds = Array.isArray(correctData?.correct_answer_ids)
      ? correctData.correct_answer_ids
      : undefined;
    return [...list]
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((a) => ({
        id: a.id,
        text: a.text || '',
        is_correct: correctIds ? correctIds.includes(a.id) : !!a.is_correct,
      }));
  };

  const initials = isEdit
    ? {
        text: question?.text,
        answers: mapInitialAnswers(question),
      }
    : {
        text: undefined,
        answers: [{ text: '', is_correct: false }],
      };

  const answersLocked = isEdit && (question?.student_answer_count || 0) > 0;

  const title = isEdit ? t('Úprava otázky') : t('Pridanie otázky');

  const onSubmit = (values) => {
    const includeAnswers = !answersLocked; // do not send answers if locked
    const answers =
      includeAnswers && Array.isArray(values.answers)
        ? values.answers
            .filter((a) => a && typeof a.text === 'string' && a.text.trim() !== '')
            .map((a, idx) => {
              const answer = {
                text: a.text.trim(),
                is_correct: !!a.is_correct,
                position: idx + 1,
              };
              // Include id ONLY for existing answers when editing (not on create)
              if (isEdit && a.id) {
                answer.id = a.id;
              }
              return answer;
            })
        : undefined;

    const body = { text: values.text };

    if (includeAnswers) {
      body.answers = answers || [];
    }

    if (isEdit) {
      saveQuestion({ id, body });
    } else {
      saveQuestion({ fv_quiz_id: quizId, body });
    }
  };

  const formKey = isEdit
    ? `question-${id}-${!!correctData}`
    : 'create-question';

  const displayedQuizId = isEdit ? question?.fv_quiz_id : quizId;

  return (
    <>
      <AppBarCMS
        title={title}
        backUrl={-1} // using -1 instead of backUrl to avoid issues after save when going back to quiz edit page
        backUrlTooltip={t('Kvízy')}
        permissionsBasic="quiz-questions"
      />
      <div style={{ margin: '10px' }}>
        <QuestionForm
          key={formKey}
          initials={initials}
          quizOptions={quizOptions}
          quizValue={displayedQuizId}
          onSubmit={onSubmit}
          answersLocked={answersLocked}
        />
      </div>
    </>
  );
};

export default QuestionsEdit;
